import {
  WorldBookSchema,
  type LLMMessage,
  type WorldBook,
  type WorldBookEntry,
} from '@/IsekaiRPG/modules/llm/llmTypes';
import type { SplitWorldBookEntries, STPromptCompilerTraceEntry } from './types';

type WorldBookTemporalState = {
  stickyRemaining: number;
  cooldownRemaining: number;
  delayRemaining: number;
  pendingDelayedActivation: boolean;
};

export class WorldBookMatcher {
  private static worldBookRuntimeState = new Map<string, WorldBookTemporalState>();

  static collectMatchedWorldBookEntries(
    runtimeMessages: LLMMessage[],
    worldBook: WorldBook | undefined,
    generationType: string,
    characterContext: {
      names: string[];
      tags: string[];
    },
    rng?: () => number,
  ): { matchedEntries: WorldBookEntry[]; traceEntries: STPromptCompilerTraceEntry[] } {
    if (!worldBook) {
      return { matchedEntries: [], traceEntries: [] };
    }

    const normalizedWorldBook = WorldBookSchema.parse(worldBook);
    if (
      !normalizedWorldBook.enabled ||
      !Array.isArray(normalizedWorldBook.entries) ||
      normalizedWorldBook.entries.length === 0
    ) {
      return { matchedEntries: [], traceEntries: [] };
    }

    const enabledEntries = normalizedWorldBook.entries.filter(entry => entry.enabled);
    const globalSettings = normalizedWorldBook.global_settings;
    const matched: WorldBookEntry[] = [];
    const matchedIndexes = new Set<number>();
    const traceEntries: STPromptCompilerTraceEntry[] = enabledEntries.map(entry => ({
      id: entry.id,
      name: entry.name,
      matched: false,
      round: -1,
      reason: '未命中',
      mode: entry.injection_mode,
      depth: entry.injection_depth,
      order: entry.injection_order,
    }));
    const recursionSources: string[] = [runtimeMessages.map(message => message.content).join('\n')];
    let round = 0;
    const temporalEvaluated = new Set<number>();
    const forcedMatched = new Set<number>();
    const temporalStateCache = new Map<number, WorldBookTemporalState>();
    this.pruneWorldBookRuntimeState(enabledEntries.map(entry => entry.id));

    const maxRounds = globalSettings.recursive_scan
      ? Math.max(0, globalSettings.max_recursion_rounds ?? enabledEntries.length)
      : 0;

    while (round <= maxRounds) {
      let roundMatched = false;

      for (let index = 0; index < enabledEntries.length; index += 1) {
        const entry = enabledEntries[index];
        if (matchedIndexes.has(index)) {
          continue;
        }

        let state = temporalStateCache.get(index);
        if (!state) {
          state = this.getWorldBookRuntimeState(entry.id);
          temporalStateCache.set(index, state);
        }

        if (!temporalEvaluated.has(index)) {
          temporalEvaluated.add(index);
          this.tickWorldBookRuntimeState(state);

          if (state.pendingDelayedActivation && state.delayRemaining <= 0) {
            forcedMatched.add(index);
          } else if (state.pendingDelayedActivation) {
            traceEntries[index].reason = `延迟触发中（剩余 ${state.delayRemaining}）`;
          } else if (state.stickyRemaining > 0) {
            forcedMatched.add(index);
            state.stickyRemaining -= 1;
          } else if (state.cooldownRemaining > 0) {
            traceEntries[index].reason = `冷却中（剩余 ${state.cooldownRemaining}）`;
          }
        }

        if (forcedMatched.has(index)) {
          matchedIndexes.add(index);
          matched.push(entry);
          roundMatched = true;
          traceEntries[index].matched = true;
          traceEntries[index].round = round;
          traceEntries[index].reason = state.pendingDelayedActivation ? '延迟触发生效' : '粘性持续生效';
          state.pendingDelayedActivation = false;
          state.delayRemaining = 0;
          state.cooldownRemaining = Math.max(state.cooldownRemaining, entry.cooldown);
          if (!entry.prevent_recursion) {
            const content = entry.content.trim();
            if (content) recursionSources.push(content);
          }
          continue;
        }

        if (state.pendingDelayedActivation || state.cooldownRemaining > 0) {
          continue;
        }

        if (!this.matchCharacterFilter(entry, characterContext)) {
          traceEntries[index].reason = '角色过滤不匹配';
          continue;
        }
        if (entry.delay_until_recursion && round === 0) {
          traceEntries[index].reason = '延迟到递归轮';
          continue;
        }
        if (entry.exclude_recursion && round > 0) {
          traceEntries[index].reason = '递归轮排除';
          continue;
        }
        if (!this.isWorldBookTriggerMatched(entry, generationType)) {
          traceEntries[index].reason = 'generation_trigger 不匹配';
          continue;
        }
        if (!this.isWorldBookProbabilityHit(entry, rng)) {
          traceEntries[index].reason = '概率未命中';
          continue;
        }
        const matchSource = this.getWorldBookMatchSource(
          entry,
          globalSettings.scan_depth,
          runtimeMessages,
          recursionSources,
        );
        if (!this.matchWorldBookEntry(entry, matchSource)) {
          traceEntries[index].reason = entry.constant ? '常驻条目命中失败（异常）' : '关键词未命中';
          continue;
        }

        if (entry.delay > 0) {
          state.pendingDelayedActivation = true;
          state.delayRemaining = entry.delay;
          traceEntries[index].reason = `命中后延迟触发（${entry.delay}）`;
          continue;
        }

        matchedIndexes.add(index);
        matched.push(entry);
        roundMatched = true;
        traceEntries[index].matched = true;
        traceEntries[index].round = round;
        traceEntries[index].reason = entry.constant ? '常驻条目命中' : '关键词命中';
        state.cooldownRemaining = entry.cooldown;
        state.stickyRemaining = entry.sticky;

        if (!entry.prevent_recursion) {
          const content = entry.content.trim();
          if (content) {
            recursionSources.push(content);
          }
        }
      }

      if (!roundMatched) break;
      round += 1;
    }

    return {
      matchedEntries: matched,
      traceEntries,
    };
  }

  static splitWorldBookEntries(matchedEntries: WorldBookEntry[]): SplitWorldBookEntries {
    return {
      relativeBefore: matchedEntries
        .filter(entry => entry.injection_mode === 'relative_before')
        .sort((a, b) => a.injection_order - b.injection_order),
      relativeAfter: matchedEntries
        .filter(entry => entry.injection_mode === 'relative_after')
        .sort((a, b) => a.injection_order - b.injection_order),
      beforeExample: matchedEntries
        .filter(entry => entry.injection_mode === 'before_example')
        .sort((a, b) => a.injection_order - b.injection_order),
      afterExample: matchedEntries
        .filter(entry => entry.injection_mode === 'after_example')
        .sort((a, b) => a.injection_order - b.injection_order),
      beforeAuthorNote: matchedEntries
        .filter(entry => entry.injection_mode === 'before_author_note')
        .sort((a, b) => a.injection_order - b.injection_order),
      afterAuthorNote: matchedEntries
        .filter(entry => entry.injection_mode === 'after_author_note')
        .sort((a, b) => a.injection_order - b.injection_order),
      depthEntries: matchedEntries
        .filter(entry => entry.injection_mode === 'in_chat' || entry.injection_mode === 'depth')
        .sort((a, b) => a.injection_order - b.injection_order),
      outletEntries: matchedEntries
        .filter(entry => entry.injection_mode === 'outlet')
        .sort((a, b) => a.injection_order - b.injection_order),
    };
  }

  private static getWorldBookMatchSource(
    entry: WorldBookEntry,
    globalScanDepth: number | null,
    runtimeMessages: LLMMessage[],
    recursionSources: string[],
  ): string {
    const scanDepth = entry.scan_depth ?? globalScanDepth ?? null;
    const runtimeSource =
      scanDepth === null ? runtimeMessages : runtimeMessages.slice(Math.max(0, runtimeMessages.length - scanDepth));

    const runtimeText = runtimeSource.map(message => message.content).join('\n');
    const recursionText = recursionSources.slice(1).join('\n');
    return [runtimeText, recursionText].filter(Boolean).join('\n');
  }

  private static matchCharacterFilter(
    entry: WorldBookEntry,
    characterContext: {
      names: string[];
      tags: string[];
    },
  ): boolean {
    const filter = entry.character_filter;
    if (!filter) return true;

    const names = filter.names.map(item => item.trim()).filter(Boolean);
    const tags = filter.tags.map(item => item.trim()).filter(Boolean);
    if (names.length === 0 && tags.length === 0) return true;

    const contextNames = new Set(characterContext.names.map(item => item.toLowerCase()));
    const contextTags = new Set(characterContext.tags.map(item => item.toLowerCase()));
    const matchedName = names.some(name => contextNames.has(name.toLowerCase()));
    const matchedTag = tags.some(tag => contextTags.has(tag.toLowerCase()));
    const matched = matchedName || matchedTag;

    return filter.isExclude ? !matched : matched;
  }

  private static getWorldBookRuntimeState(entryId: string): WorldBookTemporalState {
    const existing = this.worldBookRuntimeState.get(entryId);
    if (existing) return existing;

    const created = {
      stickyRemaining: 0,
      cooldownRemaining: 0,
      delayRemaining: 0,
      pendingDelayedActivation: false,
    };
    this.worldBookRuntimeState.set(entryId, created);
    return created;
  }

  private static tickWorldBookRuntimeState(state: WorldBookTemporalState): void {
    if (state.cooldownRemaining > 0) {
      state.cooldownRemaining -= 1;
    }
    if (state.pendingDelayedActivation && state.delayRemaining > 0) {
      state.delayRemaining -= 1;
    }
  }

  private static pruneWorldBookRuntimeState(activeEntryIds: string[]): void {
    const activeSet = new Set(activeEntryIds);
    for (const key of this.worldBookRuntimeState.keys()) {
      if (!activeSet.has(key)) {
        this.worldBookRuntimeState.delete(key);
      }
    }
  }

  private static isWorldBookTriggerMatched(entry: WorldBookEntry, generationType: string): boolean {
    if (!Array.isArray(entry.generation_trigger) || entry.generation_trigger.length === 0) {
      return true;
    }

    return entry.generation_trigger.map(trigger => trigger.trim().toLowerCase()).includes(generationType);
  }

  private static matchWorldBookEntry(entry: WorldBookEntry, text: string): boolean {
    if (entry.constant) {
      return true;
    }

    const source = entry.case_sensitive ? text : text.toLowerCase();
    const keys = (entry.keys ?? []).map(key => key.trim()).filter(Boolean);
    if (keys.length === 0) {
      return false;
    }

    const normalizedKeys = entry.case_sensitive ? keys : keys.map(key => key.toLowerCase());
    const primaryMatched =
      entry.match_logic === 'all'
        ? normalizedKeys.every(key => this.matchToken(source, key, entry.whole_word))
        : normalizedKeys.some(key => this.matchToken(source, key, entry.whole_word));

    if (!primaryMatched) {
      return false;
    }

    const secondary = (entry.secondary_keys ?? []).map(key => key.trim()).filter(Boolean);
    if (secondary.length === 0) {
      return true;
    }

    const normalizedSecondary = entry.case_sensitive ? secondary : secondary.map(key => key.toLowerCase());
    return normalizedSecondary.some(key => this.matchToken(source, key, entry.whole_word));
  }

  private static isWorldBookProbabilityHit(entry: WorldBookEntry, rng?: () => number): boolean {
    if (!entry.use_probability) {
      return true;
    }

    const probability = Math.max(0, Math.min(100, entry.probability));
    if (probability >= 100) return true;
    if (probability <= 0) return false;
    const random = rng ?? Math.random;
    return random() * 100 < probability;
  }

  private static matchToken(source: string, token: string, wholeWord: boolean): boolean {
    if (!wholeWord) {
      return source.includes(token);
    }

    const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`\\b${escaped}\\b`);
    return pattern.test(source);
  }
}
